import { api } from "./api";
import type {
  CancelPortServicePayload,
  ChangePortServiceStatusPayload,
  CreatePortServicePayload,
  PortService,
  PortServiceDocument,
  PortServicesApiResponse,
  PortServicesDocumentsPayload,
  PortServicesFilterPayload,
  PortServicesStatusLog,
  ShipToShipVesselCallValidationResponse,
  UpdatePortServiceTimesPayload,
} from "./port-services/types";

export const getPortServices = async ({
  service,
  page = 0,
  pageSize = 25,
  filters,
}: {
  service: string;
  page?: number;
  pageSize?: number;
  filters: PortServicesFilterPayload;
}) => {
  const data: PortServicesApiResponse = await api.post(
    service,
    `port-services-search?pageSize=${pageSize}&pageNumber=${page}`,
    {
      ...filters,
    },
  );

  return {
    items: data.items,
    totalElements: data.totalElements,
  };
};

export const getPortServicesByIds = async ({
  service,
  ids,
  page = 0,
  pageSize = 50,
}: {
  service: string;
  ids: string[];
  page?: number;
  pageSize?: number;
}) => {
  if (!ids.length) {
    return [];
  }

  const data: PortServicesApiResponse = await api.post(
    service,
    `port-services-search?pageSize=${pageSize}&pageNumber=${page}`,
    {
      ids,
    },
  );

  return data.items;
};

export const submitPortServiceRequest = async ({
  service,
  portServicePayload,
  transactionId,
}: {
  service: string;
  portServicePayload: CreatePortServicePayload;
  transactionId?: string;
}) => {
  return api.post(
    service,
    `port-services`,
    portServicePayload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const getPortServiceById = async (service: string, id: string) => {
  return api.get<PortService>(service, `port-services/${id}`);
};

export const validateShipToShipVesselCall = async (service: string, imo: string) => {
  return api.post<ShipToShipVesselCallValidationResponse>(
    service,
    `port-services/is-vessel-call-valid/${encodeURIComponent(imo)}`,
  );
};

export const validatePortServiceVesselCall = async (service: string, imo: string) => {
  return api.post<ShipToShipVesselCallValidationResponse>(
    service,
    `port-services/is-vessel-call-valid/${encodeURIComponent(imo)}`,
  );
};

export const cancelPortService = async (
  service: string,
  id: string,
  payload: CancelPortServicePayload,
  transactionId?: string,
) => {
  return api.post<CancelPortServicePayload>(
    service,
    `port-services/${id}/cancel`,
    { ...payload },
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const changePortServiceStatus = async (
  service: string,
  id: string,
  payload: ChangePortServiceStatusPayload,
  transactionId?: string,
) => {
  return api.post<ChangePortServiceStatusPayload>(
    service,
    `port-services/${id}/status`,
    { ...payload },
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const renewNOC = async (service: string, id: string, transactionId?: string) => {
  return api.post<PortServiceDocument>(
    service,
    `port-services/${id}/nocs/renew`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );
};

export const getPortServicesStatusLogs = async (service: string, id: string) => {
  return api.get<PortServicesStatusLog[]>(service, `port-services/${id}/audit-records`);
};

export const sendPortServicesDocuments = (
  service: string,
  payload: PortServicesDocumentsPayload,
  transactionId?: string,
) => {
  return api.post(
    service,
    "port-services-documents/batch",
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const updatePortServiceTimes = async (
  service: string,
  id: string,
  payload: UpdatePortServiceTimesPayload,
  transactionId?: string,
) => {
  return api.patch(
    service,
    `/port-services/${id}/times-and-estimates`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};
